/**
 * The public surface of the baked SFX catalog — the ids, the lookup and the loop-marker census.
 *
 * The catalog **data** and all of the per-cue evidence live in `simsnds-catalog.ts`; this module
 * only derives from it, so that "which cue means what and why" stays one long, readable file and
 * "how the rest of the app reaches it" stays a short one. Every consumer (`sfx-map.ts`,
 * `engine.ts`, the tests) imports from here.
 */

import { RAW_CUES } from "./simsnds-catalog.ts";

export type { SfxCueMeta } from "./simsnds-catalog.ts";
import type { SfxCueMeta } from "./simsnds-catalog.ts";

export type SfxCueId = keyof typeof RAW_CUES;

/**
 * `docs/formats/sound-music.md` counts **31** `SIMSNDS.SNG` resources; the bake manifest carries
 * only indices 0–29 (30 files). Not this lane's bake pipeline to fix (data/baked is read-only
 * here) — noted so the gap isn't mistaken for a mapping bug. `SIMSNDS-30`'s absence just means one
 * fewer id in the pool.
 */
export const SIMSNDS_UNBAKED_COUNT = 1;

/**
 * VERIFIED (`sound-music.md`'s "Ten cues are gate-style, not one-shots", cross-checked at runtime
 * by `packages/formats/test/sng.test.ts` against the real resource bytes): these ten `SIMSNDS`
 * ids, and no others, carry SCI's set-loop-point marker. Every `SfxCueMeta.sustainedAtEnd` in the
 * catalog must agree with this list exactly — `sfx-map.test.ts` asserts it.
 */
export const LOOP_MARKED_SIMSNDS_IDS: readonly SfxCueId[] = [
  "SIMSNDS-02",
  "SIMSNDS-03",
  "SIMSNDS-10",
  "SIMSNDS-19",
  "SIMSNDS-21",
  "SIMSNDS-23",
  "SIMSNDS-24",
  "SIMSNDS-26",
  "SIMSNDS-27",
  "SIMSNDS-28",
];

/**
 * Re-typed from `RAW_CUES`'s per-entry literal types to a uniform `SfxCueMeta` so every value (not
 * just the entries that happen to declare `duplicateOf`) exposes the same shape — the catalog's
 * `as const` is only there to keep `SfxCueId` a literal union of the 34 real ids.
 */
export const SIMSNDS_CUES: Readonly<Record<SfxCueId, SfxCueMeta>> = RAW_CUES;

export const SIMSNDS_CUE_IDS = Object.keys(RAW_CUES) as readonly SfxCueId[];

export function cuePath(id: SfxCueId): string {
  return SIMSNDS_CUES[id].path;
}
